import React, { useMemo } from "react";

import clsx from "clsx";
import { format, parseISO } from "date-fns";

import { type GetCampaign_me_adObject } from "@north-beam/nb-client-side";
import { Platforms } from "@north-beam/nb-common";

import { type Level } from "../../types";

import { BreakdownLabels } from "./breakdown-labels";
import { Hierarchy } from "./hierarchy";
import { TagettingSettings } from "./targetting-settings";

const DATE_FORMAT = "MMM dd, yyyy";

function formatDate(dateStr: string) {
  try {
    const date = parseISO(dateStr);
    return format(date, DATE_FORMAT);
  } catch {
    return dateStr;
  }
}

interface Props {
  adObject: GetCampaign_me_adObject;
  adObjectLevel: Level;
  platformLabel: string;
  onRequestReload(): Promise<void>;
  onChangeAdObject(id: string): void;
}

export const Details = ({
  adObject,
  adObjectLevel,
  platformLabel,
  onRequestReload,
  onChangeAdObject,
}: Props) => {
  const status = adObject.status;
  const createdAt = adObject.metadata.created_time;

  const idOnPlatform = adObject.idOnPlatform;

  const budget = useMemo(
    () => adObject.statusLine.find((e) => e.key === "Budget"),
    [adObject],
  );
  const bidStrategy = useMemo(
    () => adObject.statusLine.find((e) => e.key === "Bid strategy"),
    [adObject],
  );

  const nbtPlatformID = adObject.nbtPlatformID;
  const googleTargetting = adObject.formattedGoogleData?.targettingSetting;
  const facebookTargetting = adObject.formattedFacebookData?.targettingSetting;
  const tiktokTargetting = adObject.formattedTiktokData?.targettingSetting;
  const pinterestTargetting =
    adObject.formattedPinterestData?.targettingSetting;

  let targetingSettings: any = null;

  if (nbtPlatformID === Platforms.ADWORDS && googleTargetting) {
    targetingSettings = googleTargetting;
  } else if (nbtPlatformID === Platforms.FB && facebookTargetting) {
    targetingSettings = facebookTargetting;
  } else if (nbtPlatformID === Platforms.TIKTOK && tiktokTargetting) {
    targetingSettings = tiktokTargetting;
  } else if (nbtPlatformID === Platforms.PINTEREST && pinterestTargetting) {
    targetingSettings = pinterestTargetting;
  }

  return (
    <>
      {(status || createdAt || idOnPlatform) && (
        <div className="mt-8">
          <h3 className="text-lg font-bold">Details</h3>
          {status && (
            <div className="flex items-center h-8">
              <div className="shrink-0">Status</div>
              <div className="flex-1 mx-3">
                <div className="border-b border-b-gray-2 border-dashed"></div>
              </div>
              <div
                className={clsx("shrink-0 font-light rounded py-0.5 px-2.5", {
                  "text-alien+2 bg-alien-2": status === "Active",
                  "bg-interstellar-3": status !== "Active",
                })}
              >
                {status}
              </div>
            </div>
          )}
          {createdAt && (
            <div className="flex items-center h-8">
              <div className="shrink-0">Created on</div>
              <div className="flex-1 mx-3">
                <div className="border-b border-b-gray-2 border-dashed"></div>
              </div>
              <div className="shrink-0 font-bold">{formatDate(createdAt)}</div>
            </div>
          )}
          {idOnPlatform && (
            <div className="flex items-center h-8">
              <div className="shrink-0">In-platform ID</div>
              <div className="flex-1 mx-3">
                <div className="border-b border-b-gray-2 border-dashed"></div>
              </div>
              <div className="shrink-0 font-bold">{idOnPlatform}</div>
            </div>
          )}
        </div>
      )}
      {(budget || bidStrategy) && (
        <div className="mt-8">
          <h3 className="text-lg font-bold">Bid and budget</h3>
          {budget && (
            <div className="flex items-center h-8">
              <div className="shrink-0">Budget</div>
              <div className="flex-1 mx-3">
                <div className="border-b border-b-gray-2 border-dashed"></div>
              </div>
              <div className="shrink-0 font-bold">{budget.value}</div>
            </div>
          )}
          {bidStrategy && (
            <div className="flex items-center h-8">
              <div className="shrink-0">Bid strategy</div>
              <div className="flex-1 mx-3">
                <div className="border-b border-b-gray-2 border-dashed"></div>
              </div>
              <div className="shrink-0 font-bold">{bidStrategy.value}</div>
            </div>
          )}
        </div>
      )}
      {adObject.hierarchy && (
        <Hierarchy
          adObject={adObject}
          adObjectLevel={adObjectLevel}
          platformLabel={platformLabel}
          onChangeLevel={onChangeAdObject}
        />
      )}
      {adObject.labelsDigest && (
        <BreakdownLabels
          adObject={adObject}
          adObjectLevel={adObjectLevel}
          onRequestReload={onRequestReload}
        />
      )}
      {targetingSettings && <TagettingSettings settings={targetingSettings} />}
    </>
  );
};
